import {
  informationFieldConfigValueSchema,
  usePatientInformationFields,
  useProspectPatients,
} from "@/api/Patients";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
  patientStatusSchema,
} from "@medoma/models/patients";
import { Loading } from "@medoma/components/Loading/Loading";
import { Trans, useLingui } from "@lingui/react/macro";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import styles from "./PatientsList.module.scss";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { NavLink, useLocation } from "react-router";
import NoResults from "@/components/NoResults/NoResults";
import { filterByNameTerms } from "@medoma/components/SearchField/searchUtils";
import { usePatientNameFilter } from "./DailyOverviewContext";
import { useCallback, useMemo, type CSSProperties } from "react";
import { humanFormat } from "@medoma/models/date-and-time";
import { useSortState } from "@/components/SortableColumnHeader/useSortState";
import { SortableColumnHeader } from "@/components/SortableColumnHeader/SortableColumnHeader";
import { useSortedItems } from "@/components/SortableColumnHeader/useSortedItems";

type ProspectSortColumn = "name" | "area" | "registeredAt" | "plannedAdmission";

const ProspectPatientsList = () => {
  const { t } = useLingui();
  const { search } = useLocation();
  const {
    data: prospectPatients,
    isPending,
    isError,
    error,
  } = useProspectPatients({});
  const {
    data: fieldsConfig,
    isPending: isPendingFieldsConfig,
    isError: isErrorFieldsConfig,
    error: fieldsConfigError,
  } = usePatientInformationFields();
  const { value: patientNameFilter } = usePatientNameFilter();
  const { sortState, toggleSort } = useSortState<ProspectSortColumn>({
    column: "name",
    direction: "asc",
  });

  const filteredPatients = useMemo(() => {
    if (!prospectPatients) {
      return [];
    }
    return prospectPatients.filter((patient) =>
      filterByNameTerms(getPatientFullName(patient.name), patientNameFilter),
    );
  }, [prospectPatients, patientNameFilter]);
  type ProspectPatient = (typeof filteredPatients)[number];
  const compareProspects = useCallback(
    (a: ProspectPatient, b: ProspectPatient, column: ProspectSortColumn) => {
      switch (column) {
        case "name":
          return getPatientFullName(a.name).localeCompare(
            getPatientFullName(b.name),
          );
        case "area":
          return (a.area?.displayName ?? "").localeCompare(
            b.area?.displayName ?? "",
          );
        case "registeredAt":
          return a.registeredAt.getTime() - b.registeredAt.getTime();
        case "plannedAdmission": {
          const dateA = a.plannedAdmissionDateTime;
          const dateB = b.plannedAdmissionDateTime;
          if (!dateA && !dateB) {
            return 0;
          }
          if (!dateA) {
            return 1;
          }
          if (!dateB) {
            return -1;
          }
          return dateA.getTime() - dateB.getTime();
        }
      }
    },
    [],
  );
  const visiblePatients = useSortedItems(
    filteredPatients,
    sortState,
    compareProspects,
  );

  if (isPending || isPendingFieldsConfig) {
    return <Loading message={t`Hämtar patienter`} padding={24} />;
  }

  if (isError || isErrorFieldsConfig) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patienter.`} ${deducedError(error ?? fieldsConfigError)}`}
        padding={24}
      />
    );
  }

  if (prospectPatients.length === 0) {
    return <NoResults message={t`Inga patienter hittades`} padding={24} />;
  }

  if (visiblePatients.length === 0) {
    return (
      <NoResults message={t`Inga patienter matchar filtret`} padding={24} />
    );
  }

  const hiddenValue = informationFieldConfigValueSchema.enum.hidden;
  const showPlaceOfAssessment = fieldsConfig.PlaceOfAssessment !== hiddenValue;
  const showReasonForAdmission =
    fieldsConfig.ReasonForAdmission !== hiddenValue;
  const showCareSummary = fieldsConfig.CareSummary !== hiddenValue;
  const showPlannedAdmission =
    fieldsConfig.PlannedAdmissionDateTime !== hiddenValue;

  const columnCount =
    3 + // name + area + registeredAt
    (showPlaceOfAssessment ? 1 : 0) +
    (showReasonForAdmission ? 1 : 0) +
    (showCareSummary ? 1 : 0) +
    (showPlannedAdmission ? 1 : 0);

  return (
    <nav aria-label={t`Patientlista`} className={styles.patientsListNav}>
      <ul
        className={styles.patientsList}
        style={{ "--column-count": columnCount } as CSSProperties}
      >
        <li className={styles.patientsListHeader}>
          <SortableColumnHeader
            columnId="name"
            sortState={sortState}
            toggleSort={toggleSort}
          >
            <Trans>Namn</Trans>
          </SortableColumnHeader>
          <SortableColumnHeader
            columnId="area"
            sortState={sortState}
            toggleSort={toggleSort}
          >
            <Trans>Område</Trans>
          </SortableColumnHeader>
          {showPlaceOfAssessment ? (
            <Text element="span" weight="medium" size="small">
              <Trans>Plats för bedömning</Trans>
            </Text>
          ) : null}
          {showReasonForAdmission ? (
            <Text element="span" weight="medium" size="small">
              <Trans>Inskrivningsorsak</Trans>
            </Text>
          ) : null}
          {showCareSummary ? (
            <Text element="span" weight="medium" size="small">
              <Trans>Vårdsammanfattning</Trans>
            </Text>
          ) : null}
          <SortableColumnHeader
            columnId="registeredAt"
            sortState={sortState}
            toggleSort={toggleSort}
          >
            <Trans>Registrerad</Trans>
          </SortableColumnHeader>
          {showPlannedAdmission ? (
            <SortableColumnHeader
              columnId="plannedAdmission"
              sortState={sortState}
              toggleSort={toggleSort}
            >
              <Trans>Planerad inskrivning</Trans>
            </SortableColumnHeader>
          ) : null}
        </li>
        {visiblePatients.map((patient) => (
          <li key={patient.id}>
            <NavLink
              to={{ pathname: patient.id, search }}
              className={styles.patient}
            >
              <span className={styles.nameAndPIN}>
                <Text element="span" weight="medium">
                  {getPatientFullName(patient.name)}
                </Text>
                <Text element="span" size="small" color="faded">
                  {formatPersonalIdentityNumber({
                    personalIdentityNumber: patient.personalIdentityNumber,
                    personalIdentityNumberType:
                      patient.personalIdentityNumberType,
                  })}
                </Text>
              </span>
              <Text element="span">{patient.area?.displayName ?? "–"}</Text>
              {showPlaceOfAssessment ? (
                <Text element="span">{patient.placeOfAssessment ?? "–"}</Text>
              ) : null}
              {showReasonForAdmission ? (
                patient.reasonForAdmission ? (
                  <TextWithLineBreaks>
                    {patient.reasonForAdmission}
                  </TextWithLineBreaks>
                ) : (
                  <Text element="span">–</Text>
                )
              ) : null}
              {showCareSummary ? (
                patient.careSummary ? (
                  <TextWithLineBreaks>{patient.careSummary}</TextWithLineBreaks>
                ) : (
                  <Text element="span">–</Text>
                )
              ) : null}
              <Text element="span">
                {patient.status === patientStatusSchema.enum.prospect
                  ? humanFormat(patient.registeredAt, { dateStyle: "short" })
                  : "–"}
              </Text>
              {showPlannedAdmission ? (
                <Text element="span">
                  {patient.plannedAdmissionDateTime
                    ? humanFormat(patient.plannedAdmissionDateTime, {
                        dateStyle: "short",
                      })
                    : "–"}
                </Text>
              ) : null}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export { ProspectPatientsList };
